import {
    faExclamationCircle,
    faExclamationTriangle,
    faFire,
    IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import {
    ApplicationRoleCode,
    IIssueActivityUserChild,
    IIssueChild,
    IIssueHardwareHealthResponse,
    IIssueResponseBase,
    IssueHardwareType,
    IssueMachineHealthResponse,
    IssueRemoveReason,
    IssueSavingsStatus,
    IssueStatusType,
    IUserResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { subDays, subHours, subMonths, subWeeks } from 'date-fns';
import { IssueUserInfo } from 'pages/Desk/issues/types';
import { SeverityFilter, TimeRangeFilter } from 'shared/issue_filters_context/types';
import styled from 'styled-components';
import { sortBy, toMap, uniqueBy } from 'utils/array';
import { isNodeInactive } from 'utils/node';
import { Filter, FilterExpr } from 'utils/query_v3';

export const IssueActivityFeedPollingMs = 1000 * 60; // 60 seconds in milliseconds
export const IssuePollingMs = 1000 * 60; // 60 seconds in milliseconds

export const IssueDescriptionMaxLength = 5000;
export const IssueRecommendedActionMaxLength = 1000;
export const IssueCommentMaxLength = 10000;
export const IssueArchiveCommentMaxLength = 1000;

export const SevereIssueSeverityThreshold = 0.6;
export const LowIssueSeverityThreshold = 0.3;

export const IssuesDefaultSort: DataSourceSort[] = [
    { field: 'Severity', dir: 'desc' },
    { field: 'Priority', dir: 'desc' },
    { field: 'Created', dir: 'asc' },
];

export type IssueFilter = Readonly<{
    id?: string;
    locationId?: string;
    issueNumber?: number;
    description?: string;
    statusName?: string;
    statusType?: IssueStatusType[];
    priority?: number[];
    severity?: SeverityFilter[];
    faultTypeIds?: string[];
    savings?: IssueSavingsStatus[];
    userId?: string;
    userEmail?: string;
    createdBy?: string;
    created?: Date | TimeRangeFilter;
    lastActivity?: Date;
    nameSearch?: string;
    hierarchyGroupIds?: string[];
    dynamicSystemIds?: string[];
    hasHealthRanges?: boolean;
    hardwareTypes?: IssueHardwareType[];
    isInternal?: boolean;
    hardwareId?: number;
    isFlagged?: boolean;
    shouldReceiveNotifications?: boolean;
    manuallyCreated?: boolean;
    isResolved?: boolean;

    // Optional filter suffix expression (to allow using mixed and/or)
    suffixExpression?: string;
}>;

const getDurationFilter = (timeRangeFilter: Date | TimeRangeFilter): Date => {
    if (timeRangeFilter instanceof Date) {
        return timeRangeFilter;
    }
    switch (timeRangeFilter) {
        case 'past-12-hours':
            return subHours(new Date(), 12);

        case 'past-day':
            return subDays(new Date(), 1);

        case 'past-week':
            return subWeeks(new Date(), 1);

        case 'past-month':
            return subMonths(new Date(), 1);

        default:
            throw new Error(`Unsupported time range filter: ${timeRangeFilter}`);
    }
};

const getResolvedFilter = (isResolved: boolean): FilterExpr => {
    if (isResolved) {
        return Filter.isNotNull('DateResolved');
    }
    return Filter.isNull('DateResolved');
};

export const buildFilterExpression = (
    mode: IssueMode,
    {
        id,
        locationId,
        issueNumber,
        description,
        statusName,
        statusType,
        priority,
        severity,
        faultTypeIds,
        savings,
        userId,
        userEmail,
        createdBy,
        created,
        lastActivity,
        hasHealthRanges,
        hardwareTypes,
        dynamicSystemIds,
        isInternal,
        hardwareId,
        isFlagged,
        shouldReceiveNotifications,
        manuallyCreated,
        isResolved,
        nameSearch,
        suffixExpression,
    }: Partial<IssueFilter>,
    filterAggregate?: 'and' | 'or'
) => {
    const aggregateFn =
        filterAggregate === undefined || filterAggregate === 'and'
            ? Filter.and
            : Filter.or;

    return aggregateFn(
        id ? Filter.eq('Id', id) : undefined,
        locationId ? Filter.eq('LocationId', locationId) : undefined,
        issueNumber ? Filter.eq('IssueNumber', issueNumber) : undefined,
        description ? Filter.eq('Description', description) : undefined,
        priority ? Filter.in('Priority', priority) : undefined,
        statusName ? Filter.eq('StatusName', statusName) : undefined,
        statusType ? Filter.in('StatusType', statusType) : undefined,
        severity
            ? Filter.or(
                  ...severity.map((value) => {
                      switch (value) {
                          // severe severity: > 0.6
                          case 'severe':
                              return Filter.gt('Severity', SevereIssueSeverityThreshold);
                          // low severity: < 0.3
                          case 'low':
                              return Filter.lt('Severity', LowIssueSeverityThreshold);
                          // moderate severity: >= 0.3 and =< 0.6
                          default:
                              return Filter.and(
                                  Filter.geq('Severity', LowIssueSeverityThreshold),
                                  Filter.leq('Severity', SevereIssueSeverityThreshold)
                              );
                      }
                  })
              )
            : undefined,
        faultTypeIds ? Filter.anyIn('FaultTypes.Id', faultTypeIds) : undefined,
        savings ? Filter.in('IssueSavingsStatus', savings) : undefined,
        userId ? Filter.eq('UserId', userId) : undefined,
        userEmail ? Filter.eq('UserEmail', userEmail) : undefined,
        createdBy ? Filter.eq('CreatedBy', createdBy) : undefined,
        created ? Filter.geq('Created', getDurationFilter(created)) : undefined,
        lastActivity ? Filter.geq('LastActivity', lastActivity) : undefined,
        hasHealthRanges !== undefined
            ? Filter.eq('HasHealthRanges', hasHealthRanges)
            : undefined,
        hardwareTypes ? Filter.in('IssueHardwareType', hardwareTypes) : undefined,
        dynamicSystemIds ? Filter.in('DynamicSystem.Id', dynamicSystemIds) : undefined,
        isInternal !== undefined ? Filter.eq('IsInternal', isInternal) : undefined,
        isFlagged !== undefined ? Filter.eq('IsFlagged', isFlagged) : undefined,
        shouldReceiveNotifications !== undefined
            ? Filter.eq('ShouldReceiveNotifications', shouldReceiveNotifications)
            : undefined,
        manuallyCreated !== undefined
            ? Filter.eq('ManuallyCreated', manuallyCreated)
            : undefined,
        isResolved !== undefined ? getResolvedFilter(isResolved) : undefined,
        hardwareId !== undefined
            ? Filter.or(
                  Filter.eq('BaseStation.BaseStationId', hardwareId),
                  Filter.eq('Repeater.ReceiverId', hardwareId),
                  Filter.eq('Node.NodeId', hardwareId),
                  Filter.eq('Hub.HubId', hardwareId)
              )
            : undefined,
        nameSearch
            ? Filter.or(
                  Number.isInteger(Number(nameSearch))
                      ? Filter.contains('FormattedIssueNumber', nameSearch)
                      : undefined,
                  Filter.contains('StatusName', nameSearch),
                  Filter.in('Priority', getPriorities(nameSearch)),
                  Filter.in('Severity', getSeverities(nameSearch)),
                  mode === 'hardwareHealth'
                      ? Filter.startsWith('HardwareSerialNumber', nameSearch)
                      : undefined,
                  mode === 'machineHealth'
                      ? Filter.contains('HierarchyChildren.HierarchyName', nameSearch)
                      : undefined
              )
            : undefined,
        suffixExpression
    );
};

const getPriorities = (priority: string) => {
    const priorityLowerCase = priority.toLowerCase();
    return IssuePriorities.filter(({ label }) =>
        label.toLowerCase().includes(priorityLowerCase)
    ).map(({ priorityValue }) => priorityValue);
};

const getSeverities = (severity: string) => {
    const severityLowerCase = severity.toLowerCase();
    return IssueSeverities.filter(({ label }) =>
        label.toLowerCase().includes(severityLowerCase)
    ).map(({ severityValue }) => severityValue);
};

export type IssueType = 'draft' | 'active' | 'archived';

export type IssueMode = 'machineHealth' | 'hardwareHealth';

export const DefaultIssueMode: IssueMode = 'machineHealth';

export const issueModeSupportsAutomaticMaintenanceTaskCreation = (issueMode: IssueMode) =>
    issueMode === 'machineHealth';

export type IssueSeverity = Readonly<{
    label: string;
    severityValue: number;
    icon: IconDefinition;
    backgroundColor: string;
    isMatch: (value: number) => boolean;
}>;

export const IssueSeverities: IssueSeverity[] = [
    {
        label: 'Severe',
        severityValue: 1,
        icon: faFire,
        backgroundColor: 'var(--severity-alarm)',
        isMatch: (value) => value > SevereIssueSeverityThreshold,
    },
    {
        label: 'Moderate',
        severityValue: 0.5,
        icon: faExclamationTriangle,
        backgroundColor: 'var(--severity-moderate)',
        isMatch: (value) =>
            value >= LowIssueSeverityThreshold && value <= SevereIssueSeverityThreshold,
    },
    {
        label: 'Low',
        severityValue: 0,
        icon: faExclamationCircle,
        isMatch: (value) => value < LowIssueSeverityThreshold,
        backgroundColor: 'var(--severity-low)',
    },
];

export const getIssueSeverity = (value: number): IssueSeverity => {
    const severity = IssueSeverities.find(({ isMatch: isSeverity }) => isSeverity(value));
    if (!severity) {
        throw new Error('Invalid severity');
    }
    return severity;
};

export type IssuePriority = Readonly<{
    label: string;
    priorityValue: number;
    backgroundColor: string;
    textColor: string;
}>;

export const IssuePriorities: IssuePriority[] = [
    {
        priorityValue: 1,
        label: 'Critical',
        backgroundColor: 'var(--priority-1)',
        textColor: 'var(--text-on-color)',
    },
    {
        priorityValue: 2,
        label: 'High',
        backgroundColor: 'var(--priority-2)',
        textColor: 'var(--text-on-color)',
    },
    {
        priorityValue: 3,
        label: 'Medium',
        backgroundColor: 'var(--priority-3)',
        textColor: 'var(--text-on-color)',
    },
    {
        priorityValue: 4,
        label: 'Low',
        backgroundColor: 'var(--priority-4)',
        textColor: 'var(--background-multicolors-default)',
    },
    {
        priorityValue: 5,
        label: 'Minimal',
        backgroundColor: 'var(--priority-5)',
        textColor: 'var(--background-multicolors-default)',
    },
];

export const getIssuePriority = (value: number): IssuePriority => {
    const constrainedValue = Math.max(1, Math.min(5, Math.floor(value)));

    const priority = IssuePriorities.find(
        ({ priorityValue }) => priorityValue === constrainedValue
    );

    if (!priority) {
        throw new Error('Invalid priority');
    }
    return priority;
};

export type IssueStatus = Readonly<{
    id: string;
    label: string;
    backgroundColor: string;
    textColor: string;
    type: IssueStatusType;
}>;

export const IssueStatuses: IssueStatus[] = [
    {
        id: 'draft',
        label: 'Draft',
        backgroundColor: 'var(--status-draft)',
        textColor: 'var(--text-secondary)',
        type: IssueStatusType.Draft,
    },
    {
        id: 'new',
        label: 'New',
        backgroundColor: 'var(--status-new)',
        textColor: 'var(--text-on-color)',
        type: IssueStatusType.New,
    },
    {
        id: 'open',
        label: 'Open',
        backgroundColor: 'var(--status-open)',
        textColor: 'var(--text-on-color)',
        type: IssueStatusType.Open,
    },
    {
        id: 'closed',
        label: 'Closed',
        backgroundColor: 'var(--status-closed)',
        textColor: 'var(--text-on-color)',
        type: IssueStatusType.Closed,
    },
    {
        id: 'wont-fix',
        label: "Won't Fix",
        backgroundColor: 'var(--status-wont-fix)',
        textColor: 'var(--text-on-color)',
        type: IssueStatusType.WontFix,
    },
    {
        id: 'not-an-issue',
        label: 'Not an Issue',
        backgroundColor: 'var(--status-not-an-issue)',
        textColor: 'var(--text-on-color)',
        type: IssueStatusType.NotIssue,
    },
    {
        id: 'removed',
        label: 'Removed',
        backgroundColor: 'var(--status-removed)',
        textColor: 'var(--text-on-color)',
        type: IssueStatusType.Removed,
    },
];

const issueStatusByType = toMap(IssueStatuses, (s) => s.type);

export const getIssueStatus = (type: IssueStatusType): IssueStatus => {
    const status = issueStatusByType.get(type);

    if (!status) {
        throw new Error('Invalid status');
    }
    return status;
};

export type SavingsStatus = Readonly<{
    label: string;
    type: IssueSavingsStatus;
}>;

export const IssueSavingsStatuses: SavingsStatus[] = [
    {
        label: 'Not Entered',
        type: IssueSavingsStatus.NotEntered,
    },
    {
        label: 'Entered',
        type: IssueSavingsStatus.Entered,
    },
    {
        label: 'Not Applicable',
        type: IssueSavingsStatus.NotApplicable,
    },
    {
        label: 'Verified',
        type: IssueSavingsStatus.Approved,
    },
];

export type HardwareType = Readonly<{
    label: string;
    type: IssueHardwareType;
}>;

export const IssueHardwareTypes: HardwareType[] = [
    {
        label: 'Base Station',
        type: IssueHardwareType.BaseStation,
    },
    {
        label: 'Repeater',
        type: IssueHardwareType.Repeater,
    },
    {
        label: 'IoT HUB',
        type: IssueHardwareType.Hub,
    },
    {
        label: 'Wireless Vibration Sensor',
        type: IssueHardwareType.WirelessVibrationSensor,
    },
    {
        label: 'Wired Vibration Sensor',
        type: IssueHardwareType.WiredVibrationSensor,
    },
    { label: 'IEPE Adapter', type: IssueHardwareType.IepeAdapter },
    { label: 'Analog Adapter', type: IssueHardwareType.AnalogAdapter },
];

export const getIssueSavingsStatus = (type: IssueSavingsStatus): SavingsStatus => {
    const status = IssueSavingsStatuses.find((savings) => savings.type === type);

    if (!status) {
        throw new Error('Invalid status');
    }
    return status;
};

export type IssueRemoveReasonOption = Readonly<{
    label: string;
    type: IssueRemoveReason;
}>;

export const IssueRemoveReasons: IssueRemoveReasonOption[] = [
    {
        label: 'Equipment Is Healthy',
        type: IssueRemoveReason.EquipmentIsHealthy,
    },
    {
        label: 'Issue Outdated',
        type: IssueRemoveReason.IssueOutdated,
    },
    {
        label: 'Model Oversensitive',
        type: IssueRemoveReason.ModelOversensitive,
    },
    {
        label: 'Invalid Model Assignment',
        type: IssueRemoveReason.InvalidModelAssignment,
    },
    {
        label: 'Invalid Sensor Data',
        type: IssueRemoveReason.InvalidSensorData,
    },
    {
        label: 'Duplicate Issue',
        type: IssueRemoveReason.DuplicateIssue,
    },
    {
        label: 'Other',
        type: IssueRemoveReason.Other,
    },
];

export const getIssueRemoveReason = (
    type: IssueRemoveReason
): IssueRemoveReasonOption => {
    const removeReason = IssueRemoveReasons.find((reason) => reason.type === type);

    if (!removeReason) {
        throw new Error('Invalid remove reason');
    }
    return removeReason;
};

export const getIssueId = (issue: IIssueResponseBase | IIssueChild) =>
    'IssueId' in issue ? issue.IssueId : issue.Id;

export const getFormattedIssueName = (issue: IIssueResponseBase | IIssueChild) => {
    const issueNumber = `#${issue.IssueNumber}`;
    const issueGroup =
        issue instanceof IssueMachineHealthResponse && issue.Group
            ? ` - ${issue.Group.GroupName} -`
            : '';
    const faults =
        issue.FaultTypes.length > 0
            ? sortBy(issue.FaultTypes, ({ FullName }) => FullName)
                  .map(({ Name }) => Name)
                  .join(', ')
            : 'Issue';

    return `${issueNumber}${issueGroup} ${faults}`;
};

export const isKcfUser = (role: ApplicationRoleCode) =>
    role === ApplicationRoleCode.Sales ||
    role === ApplicationRoleCode.Sentry ||
    role === ApplicationRoleCode.SystemAdmin;

export const isKcfAdmin = (role: ApplicationRoleCode) =>
    role === ApplicationRoleCode.Sentry || role === ApplicationRoleCode.SystemAdmin;

export function toIssueUserInfo(
    user: IUserResponse | IIssueActivityUserChild | IssueUserInfo
): IssueUserInfo {
    // IssueUserInfo
    if ('id' in user) {
        return user;
    }
    // IUserResponse
    if ('Id' in user) {
        return {
            id: user.Id,
            role: user.ApplicationRoleCode,
            email: user.Email,
            firstName: user.FirstName,
            lastName: user.LastName,
        };
    }
    // IIssueActivityUserChild
    if ('UserId' in user) {
        return {
            id: user.UserId,
            role: user.ApplicationRole,
            email: user.UserEmail,
            firstName: user.UserFirstName,
            lastName: user.UserLastName,
        };
    }
    throw new Error('Invalid user object');
}

export function getUniqueUserInfo(userInfo: IssueUserInfo[]): IssueUserInfo[] {
    return uniqueBy(userInfo, (user) => user.id);
}

export function sortIssueUserInfo(userInfo: IssueUserInfo[]): IssueUserInfo[] {
    return sortBy(
        userInfo,
        (user) => user.firstName,
        (user) => user.lastName,
        (user) => user.id
    );
}

export function getIssueMode(issue: IIssueResponseBase): IssueMode {
    return issue instanceof IssueMachineHealthResponse
        ? 'machineHealth'
        : 'hardwareHealth';
}

export const getReadableIssueHardwareType = (type: IssueHardwareType) => {
    switch (type) {
        case IssueHardwareType.BaseStation:
            return 'Base Station';
        case IssueHardwareType.Repeater:
            return 'Repeater';
        case IssueHardwareType.AnalogAdapter:
            return 'Analog Adapter';
        case IssueHardwareType.Hub:
            return 'IoT HUB';
        case IssueHardwareType.IepeAdapter:
            return 'IEPE Adapter';
        case IssueHardwareType.WiredVibrationSensor:
            return 'Wired Vibration Sensor';
        case IssueHardwareType.WirelessVibrationSensor:
            return 'Wireless Vibration Sensor';
        default:
            throw new Error(
                `Unknown IssueHardwareType: ${JSON.stringify(IssueHardwareType)}`
            );
    }
};

export const getHardwareHealthTooltipMessage = (issue: IIssueHardwareHealthResponse) => {
    const hardwareType = getReadableIssueHardwareType(issue.IssueHardwareType);

    if (issue.HardwareLocationMoved) {
        return `${hardwareType} has been moved from this location.`;
    }

    if (issue.Node && isNodeInactive(issue.Node.NodeStatusCode)) {
        return 'Sensor is inactive.';
    }

    return undefined;
};

export const issueGroupUnboundMessage =
    'The group for this issue was deleted during a system reconfiguration.';

export const getIssueTab = (status: IssueStatusType) => {
    switch (status) {
        case IssueStatusType.Draft:
            return 'draft';
        case IssueStatusType.Closed:
        case IssueStatusType.WontFix:
        case IssueStatusType.NotIssue:
            return 'archived';
        default:
            return 'active';
    }
};

export const DeskModuleTitle = styled.div`
    font-size: 24px;
    font-weight: 500;
    padding-right: 10px;
`;
